import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './HomePage.module.css';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import mockupImage from '../assets/images/mockup.jpg';
import { logButtonClick } from '../utils/analyticsEvents';
import { PROVIDER_TYPES } from '../config/providerTypes';

function HomePage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const providerSectionRef = useRef(null);
  const [showProviderModal, setShowProviderModal] = useState(false);

  const handleFindProvider = (providerType) => {
    logButtonClick(window.location.pathname, `find_provider_${providerType}`);
    navigate(`/search/${providerType}`);
  };

  const handleStylistSignup = () => {
    logButtonClick(window.location.pathname, 'stylist_signup');
    setShowProviderModal(true);
  };

  const handleProviderTypeSelect = (providerType) => {
    logButtonClick(window.location.pathname, `signup_provider_${providerType}`);
    setShowProviderModal(false);
    navigate(`/signup/${providerType}`);
  };

  const handleScrollToProviders = () => {
    providerSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className={styles.page}>
      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.heroContainer}>
          <div className={styles.heroContent}>
            <div className={styles.heroText}>
              <div className={styles.titleMockupRow}>
                <div className={styles.titleSubtitleWrapper}>
                  <h1>
                    {t('home.heroTitle')}<br />
                    {t('home.heroAfro')}<br />
                    {t('home.heroClicks')}
                  </h1>
                  <p className={styles.heroSubtitleMobile}>{t('home.heroSubtitle')}</p>
                </div>
                <div className={styles.heroMockupMobile}>
                  <img src="/phonemockup_version.png" alt={t('home.mockupAlt')} className={styles.mockupImageMobile} />
                </div>
              </div>
              <p className={styles.heroSubtitle}>{t('home.heroSubtitle')}</p>
              <div className={styles.ctaGroup}>
                <button type="button" className={styles.ctaPrimary} onClick={() => handleFindProvider(PROVIDER_TYPES.HAIR)}>
                  {t('home.findButton')}
                </button>
                <button
                  type="button"
                  className={styles.ctaSecondary}
                  onClick={handleStylistSignup}
                >
                  {t('home.signupButton')}
                </button>
              </div>
            </div>
            <div className={styles.heroMockup}>
              <div className={styles.phoneFrame}>
                <img src={mockupImage} alt={t('home.mockupAlt')} className={styles.mockupImage} />
              </div>
            </div>
          </div>
          </div>
        </section>

      {/* Trouve ta prestataire */}
      <section className={styles.providerTypesSection} ref={providerSectionRef}>
        <div className={styles.container}>
          <div className={styles.badgeWrapper}>
            <button
              type="button"
              className={styles.newBadge}
              onClick={handleScrollToProviders}
              aria-label={t('home.newBadgeAriaLabel')}
            >
              <span className={styles.badgeIcon}>✨</span>
              <span className={styles.badgeText}>{t('home.newBadge')}</span>
            </button>
          </div>
          <div className={styles.providerTypesGrid}>
            <div className={styles.providerCard} onClick={() => handleFindProvider(PROVIDER_TYPES.HAIR)} role="button" tabIndex={0}>
              <div className={styles.providerIconWrapper}>
                <img src="/icons/hair.png" alt={t('home.providerHair')} className={styles.providerIcon} />
              </div>
              <h3 className={styles.providerCardTitle}>{t('home.providerHair')}</h3>
            </div>

            <div className={styles.providerCard} onClick={() => handleFindProvider(PROVIDER_TYPES.NAILS)} role="button" tabIndex={0}>
              <div className={styles.providerIconWrapper}>
                <img src="/icons/nails.png" alt={t('home.providerNails')} className={styles.providerIcon} />
              </div>
              <h3 className={styles.providerCardTitle}>{t('home.providerNails')}</h3>
            </div>

            <div className={styles.providerCard} onClick={() => handleFindProvider(PROVIDER_TYPES.MAKEUP)} role="button" tabIndex={0}>
              <div className={styles.providerIconWrapper}>
                <img src="/icons/makeup.png" alt={t('home.providerMakeup')} className={styles.providerIcon} />
              </div>
              <h3 className={styles.providerCardTitle}>{t('home.providerMakeup')}</h3>
            </div>

            <div className={styles.providerCard} onClick={() => handleFindProvider(PROVIDER_TYPES.EYELASH)} role="button" tabIndex={0}>
              <div className={styles.providerIconWrapper}>
                <img src="/icons/eyelash.png" alt={t('home.providerEyelash')} className={styles.providerIcon} />
              </div>
              <h3 className={styles.providerCardTitle}>{t('home.providerEyelash')}</h3>
            </div>

            <div className={styles.providerCard} onClick={() => handleFindProvider(PROVIDER_TYPES.VENDOR)} role="button" tabIndex={0}>
              <div className={`${styles.providerIconWrapper} ${styles.providerIconWrapperVendor}`}>
                <img src="/icons/vendor.png" alt={t('home.providerSale')} className={styles.providerIcon} />
              </div>
              <h3 className={styles.providerCardTitle}>{t('home.providerSale')}</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Modal de sélection du type de prestation */}
      {showProviderModal && (
        <div className={styles.modalOverlay} onClick={() => setShowProviderModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button 
              type="button" 
              className={styles.modalCloseBtn} 
              onClick={() => setShowProviderModal(false)}
              aria-label={t('common.close')}
            >
              ×
            </button>
            <h2 className={styles.modalTitle}>{t('home.selectProviderType')}</h2>
            <div className={styles.modalProviderGrid}>
              <div 
                className={styles.modalProviderCard} 
                onClick={() => handleProviderTypeSelect(PROVIDER_TYPES.HAIR)} 
                role="button" 
                tabIndex={0}
              >
                <div className={styles.modalProviderIconWrapper}>
                  <img src="/icons/hair.png" alt={t('home.providerHair')} className={styles.modalProviderIcon} />
                </div>
                <span className={styles.modalProviderLabel}>{t('home.providerHair')}</span>
              </div>

              <div 
                className={styles.modalProviderCard} 
                onClick={() => handleProviderTypeSelect(PROVIDER_TYPES.NAILS)} 
                role="button" 
                tabIndex={0}
              >
                <div className={styles.modalProviderIconWrapper}>
                  <img src="/icons/nails.png" alt={t('home.providerNails')} className={styles.modalProviderIcon} />
                </div>
                <span className={styles.modalProviderLabel}>{t('home.providerNails')}</span>
              </div>

              <div 
                className={styles.modalProviderCard} 
                onClick={() => handleProviderTypeSelect(PROVIDER_TYPES.MAKEUP)} 
                role="button" 
                tabIndex={0}
              >
                <div className={styles.modalProviderIconWrapper}>
                  <img src="/icons/makeup.png" alt={t('home.providerMakeup')} className={styles.modalProviderIcon} />
                </div>
                <span className={styles.modalProviderLabel}>{t('home.providerMakeup')}</span>
              </div>

              <div 
                className={styles.modalProviderCard} 
                onClick={() => handleProviderTypeSelect(PROVIDER_TYPES.EYELASH)} 
                role="button" 
                tabIndex={0}
              >
                <div className={styles.modalProviderIconWrapper}>
                  <img src="/icons/eyelash.png" alt={t('home.providerEyelash')} className={styles.modalProviderIcon} />
                </div>
                <span className={styles.modalProviderLabel}>{t('home.providerEyelash')}</span>
              </div>

              <div 
                className={styles.modalProviderCard} 
                onClick={() => handleProviderTypeSelect(PROVIDER_TYPES.VENDOR)} 
                role="button" 
                tabIndex={0}
              >
                <div className={`${styles.modalProviderIconWrapper} ${styles.modalProviderIconWrapperVendor}`}>
                  <img src="/icons/vendor.png" alt={t('home.providerSale')} className={styles.modalProviderIcon} />
                </div>
                <span className={styles.modalProviderLabel}>{t('home.providerSale')}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HomePage;
